import { useMemo, useState, useEffect } from "react";
import { B, fonts, Btn, Card, SectionTitle } from "../../../components/UI";
import { fetchQuotationsForCustomer } from "../../../firebase/services";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const COLORS = [
  "#FF7A00",
  "#3B82F6",
  "#10B981",
  "#F59E0B",
  "#8B5CF6",
  "#EF4444",
  "#14B8A6",
  "#A855F7",
];

const formatCurrency = (amount) =>
  `₹${Number(amount || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (value) => {
  if (!value) return "—";
  const date =
    typeof value?.toDate === "function" ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

const getDateInputValue = (value) => {
  if (!value) return "";
  const date =
    typeof value?.toDate === "function" ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const localDate = new Date(
    date.getTime() - date.getTimezoneOffset() * 60000
  );
  return localDate.toISOString().slice(0, 10);
};

const normalizeCategory = (item, designCategoryMap) => {
  const raw =
    designCategoryMap.get(item.design_id) ||
    designCategoryMap.get(item.designId) ||
    item.categorySlug ||
    item.category_slug ||
    "uncategorized";

  return String(raw)
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

export default function CustomerDetailPage({
  customer,
  designs,
  onBack,
  onEdit,
  showFirmName = true,
  backLabel = "← Back to Customers",
}) {
  const [search, setSearch] = useState("");
  // Independent date filter for this view — no default date, so all of the
  // customer's quotations are shown until the admin actively picks a date
  // here (independent of any filter set on the All Quotations tab).
  const [dateFilter, setDateFilter] = useState("");

  // Load ALL of this customer's quotations from Firestore (not just the ones
  // currently visible in the date-bounded All Quotations tab) so the analytics
  // and history below always reflect the customer's complete activity.
  const [customerQuotations, setCustomerQuotations] = useState([]);
  const [quotationsLoading, setQuotationsLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setQuotationsLoading(true);
    (async () => {
      try {
        const list = await fetchQuotationsForCustomer(customer?.id);
        if (active) setCustomerQuotations(list);
      } catch (e) {
        console.error("[CustomerDetailPage] Failed to load customer quotations:", e);
        if (active) setCustomerQuotations([]);
      } finally {
        if (active) setQuotationsLoading(false);
      }
    })();
    return () => { active = false; };
  }, [customer?.id]);

  const totalQuotationValue = customerQuotations.reduce(
    (sum, quotation) => sum + Number(quotation.grand_total || 0),
    0
  );

  const totalOrders = customerQuotations.length;

  const avgQuotationValue = totalOrders
    ? totalQuotationValue / totalOrders
    : 0;

  const designCategoryMap = useMemo(() => {
    const map = new Map();
    (designs || []).forEach((design) => {
      const key = design.id || design.design_id || design.designId;
      if (!key) return;
      map.set(
        key,
        design.categorySlug || design.category_slug || "uncategorized"
      );
    });
    return map;
  }, [designs]);

  // ✅ VALUE-based aggregation
  const categoryValueStats = useMemo(() => {
    const map = new Map();

    for (const quotation of customerQuotations) {
      for (const item of quotation.items || []) {
        const category = normalizeCategory(item, designCategoryMap);

        const qty = Number(item.quantity) || 0;

        const rate =
          Number(item.rate) ||
          Number(item.price) ||
          Number(item.unit_price) ||
          0;

        const value =
          Number(item.total_price) ||
          Number(item.totalPrice) ||
          qty * rate;

        map.set(category, (map.get(category) ?? 0) + value);
      }
    }

    return [...map.entries()]
      .map(([category, value]) => ({ category, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [customerQuotations, designCategoryMap]);

  const filteredQuotations = customerQuotations.filter((quotation) => {
    const query = search.trim().toLowerCase();

    const matchesSearch =
      !query ||
      [
        quotation.quotation_number,
        quotation.customer_name,
        quotation.grand_total,
      ].some((field) =>
        String(field || "").toLowerCase().includes(query)
      );

    if (!matchesSearch) return false;
    if (!dateFilter) return true;

    return getDateInputValue(quotation.created_at) === dateFilter;
  });

  const totalCategoryValue = categoryValueStats.reduce(
    (sum, d) => sum + d.value,
    0
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <SectionTitle sub="Customer analytics, profile, and quotation history">
          Customer Overview
        </SectionTitle>
        <Btn variant="ghost" onClick={onBack}>
          {backLabel}
        </Btn>
      </div>

      {/* KPI Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
          gap: 12,
        }}
      >
        <Card>
          <div style={{ fontSize: 12, color: B.textMid, fontFamily: fonts.body }}>
            Total quotation value
          </div>
          <div style={{ fontSize: 24, fontWeight: 800, fontFamily: fonts.adminDisplay }}>
            {formatCurrency(totalQuotationValue)}
          </div>
        </Card>

        <Card>
          <div style={{ fontSize: 12, color: B.textMid, fontFamily: fonts.body }}>
            Total quotations
          </div>
          <div style={{ fontSize: 24, fontWeight: 800, color: B.orange }}>
            {totalOrders}
          </div>
        </Card>

        <Card>
          <div style={{ fontSize: 12, color: B.textMid, fontFamily: fonts.body }}>
            Average quotation value
          </div>
          <div style={{ fontSize: 24, fontWeight: 800, color: B.green }}>
            {formatCurrency(avgQuotationValue)}
          </div>
        </Card>
      </div>

      {/* ✅ DONUT CHART */}
      <Card>
        <SectionTitle sub="Category-wise spend distribution (₹)">
          Product Categories Bought
        </SectionTitle>

        {categoryValueStats.length === 0 ? (
          <p style={{ margin: 0, fontFamily: fonts.body, color: B.textLight }}>
            No category data available yet.
          </p>
        ) : (
          <>
            <div style={{ width: "100%", height: 300 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={categoryValueStats}
                    dataKey="value"
                    nameKey="category"
                    innerRadius={70}
                    outerRadius={110}
                    paddingAngle={2}
                  >
                    {categoryValueStats.map((entry, index) => (
                      <Cell key={index} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>

                  <Tooltip
                    formatter={(value) => {
                      const percent = ((value / totalCategoryValue) * 100).toFixed(1);
                      return [`${formatCurrency(value)} (${percent}%)`, "Spend"];
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Legend */}
            <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 6 }}>
              {categoryValueStats.map((item, index) => {
                const percent = ((item.value / totalCategoryValue) * 100).toFixed(1);

                return (
                  <div key={item.category} style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                    <span>
                      <span
                        style={{
                          display: "inline-block",
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          background: COLORS[index % COLORS.length],
                          marginRight: 6,
                        }}
                      />
                      {item.category}
                    </span>
                    <strong>{percent}%</strong>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </Card>

      {/* Customer Details */}
      <Card>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <SectionTitle sub="Personal information">
            Customer Details
          </SectionTitle>
          {onEdit && (
            <Btn onClick={onEdit} variant="primary">
              Edit Details
            </Btn>
          )}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
            gap: 10,
            fontFamily: fonts.body,
            fontSize: 13,
          }}
        >
          <div>
            <strong>Name:</strong> {customer.customer_name || "—"}
          </div>
          {showFirmName && (
            <div>
              <strong>Firm:</strong> {customer.firm_name || "—"}
            </div>
          )}
          <div>
            <strong>Mobile:</strong> {customer.mobile_number || "—"}
          </div>
          <div>
            <strong>Agent:</strong> {customer.agent_name || "—"}
          </div>
          <div>
            <strong>GST Number:</strong> {customer.gst_number || "—"}
          </div>
          <div>
            <strong>City:</strong> {customer.city || "—"}
          </div>
          <div>
            <strong>State:</strong> {customer.state || "—"}
          </div>
          <div>
            <strong>Pincode:</strong> {customer.pincode || "—"}
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <strong>Address:</strong> {customer.address || "—"}
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <strong>Transport Name:</strong> {customer.transport_name || "—"}
          </div>
        </div>
      </Card>

      {/* Quotations Table */}
      <Card>
        <SectionTitle sub="Filter by date and search quotation number/value">
          All Quotations
        </SectionTitle>

        <div
          style={{
            display: "flex",
            gap: 10,
            marginBottom: 12,
            flexWrap: "wrap",
          }}
        >
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search quotation no, amount..."
            style={{
              background: B.white,
              border: `1.5px solid ${B.border}`,
              borderRadius: 8,
              padding: "8px 13px",
              fontSize: 12,
              color: B.text,
              fontFamily: fonts.body,
              outline: "none",
              minWidth: 250,
            }}
          />

          <input
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            style={{
              background: B.white,
              border: `1.5px solid ${B.border}`,
              borderRadius: 8,
              padding: "8px 13px",
              fontSize: 12,
              color: B.text,
              fontFamily: fonts.body,
              outline: "none",
            }}
          />
        </div>

        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              minWidth: 640,
            }}
          >
            <thead>
              <tr style={{ background: B.orangeFaint }}>
                <th style={{ textAlign: "left", padding: "10px", fontSize: 11 }}>
                  Quotation No
                </th>
                <th style={{ textAlign: "left", padding: "10px", fontSize: 11 }}>
                  Date
                </th>
                <th style={{ textAlign: "left", padding: "10px", fontSize: 11 }}>
                  Items
                </th>
                <th style={{ textAlign: "left", padding: "10px", fontSize: 11 }}>
                  Amount
                </th>
              </tr>
            </thead>

            <tbody>
              {filteredQuotations.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    style={{
                      padding: 18,
                      textAlign: "center",
                      color: B.textLight,
                      fontFamily: fonts.body,
                    }}
                  >
                    {quotationsLoading ? "Loading quotations…" : "No quotations found."}
                  </td>
                </tr>
              ) : (
                filteredQuotations.map((quotation) => (
                  <tr key={quotation.id}>
                    <td
                      style={{
                        padding: "10px",
                        borderBottom: `1px solid ${B.borderLight}`,
                      }}
                    >
                      {quotation.quotation_number || "—"}
                    </td>

                    <td
                      style={{
                        padding: "10px",
                        borderBottom: `1px solid ${B.borderLight}`,
                      }}
                    >
                      {formatDate(quotation.created_at)}
                    </td>

                    <td
                      style={{
                        padding: "10px",
                        borderBottom: `1px solid ${B.borderLight}`,
                      }}
                    >
                      {quotation.items?.length || 0}
                    </td>

                    <td
                      style={{
                        padding: "10px",
                        borderBottom: `1px solid ${B.borderLight}`,
                      }}
                    >
                      {formatCurrency(quotation.grand_total)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
